import { Flame, FLAME_DEFAULT_HEIGHT, FLAME_DEFAULT_INTENSITY, FLAME_DEFAULT_WIDTH } from '../object/primitives/Flame';
import type { Scene } from '../scene/Scene';
import { composeFlameWorldPosition } from './transform';
import type { FlameSlotDef, MeshSlotDef, SceneCompositeInstance } from './types';

/**
 * Resolve the slot's authored base intensity (before any `emit` modulation).
 * Shared with the lightEmitter template so both sites agree on the same
 * default — `CompositeInstance` snapshots this value to re-multiply against
 * the live `emitLight` state each tick, and the snapshot must match what the
 * factory used at construction or the on-tick value will diverge.
 */
export function flameBaseIntensity(slot: FlameSlotDef): number {
  return slot.intensity ?? FLAME_DEFAULT_INTENSITY;
}

/**
 * Build a `Flame` instance from a composite flame-slot definition. Sibling
 * to `buildLightFromSlot` — same shape, same anchor-socket support, same
 * "caller registers with the scene" contract.
 *
 * Caller is responsible for `scene.flames.push(...)`. We don't push here so
 * the editor preview pane can build a flame in isolation without touching
 * the live scene's flame list.
 *
 * `intensity` is pre-multiplied by `emit` so the first frame (before the
 * composite tick runs) renders at the correct dim/bright level instead of
 * full brightness for one frame.
 */
export function buildFlameFromSlot(
  slot: FlameSlotDef,
  meshSlot: MeshSlotDef,
  placement: SceneCompositeInstance,
  scene: Scene,
  emit: number,
): Flame {
  const position = composeFlameWorldPosition(placement, slot, meshSlot, scene);
  // Width/height scale with the placement's uniform scale so a 2× lantern
  // gets a 2× flame. Matches the mesh slot's scale handling.
  return new Flame({
    position,
    width: (slot.width ?? FLAME_DEFAULT_WIDTH) * placement.scale,
    height: (slot.height ?? FLAME_DEFAULT_HEIGHT) * placement.scale,
    intensity: flameBaseIntensity(slot) * emit,
    baseColor: slot.baseColor,
    midColor: slot.midColor,
    tipColor: slot.tipColor,
    scrollSpeed: slot.scrollSpeed,
    taper: slot.taper,
    noiseScale: slot.noiseScale,
    seed: placement.seed,
    softness: slot.softness,
  });
}
